"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.NodesParser = void 0;
const binary_data_parser_1 = require("./binary-data-parser");
const objects_1 = require("../objects");
/**
 * Parses index data from the binary data.
 */
class NodesParser extends binary_data_parser_1.BinaryDataParser {
    constructor(data) {
        super(data);
    }
    parse(header) {
        if (!header) {
            return [];
        }
        // Because of the data parser (sequential), nodes, patches and textures must be read in this order.
        const nodes = this.parseNodes(header.nodesCount);
        const patchesData = this.parsePatches(header, nodes);
        const textures = this.parseTextures(header.texturesCount);
        const patches = this.connectTexturesToPatches(patchesData, textures);
        NodesParser.connectPatchesToNodes(nodes, patches);
        NodesParser.computeAndUpdateNodesSizes(nodes, header.signature);
        return nodes;
    }
    parseNodes(nodesCount) {
        const result = new Array(nodesCount);
        for (let nodeIndex = 0; nodeIndex < nodesCount; nodeIndex++) {
            const offset = NodesParser.PADDING * this.getUint32();
            const vertices = this.getUint16();
            const faces = this.getUint16();
            const error = this.getErrorAndSkipCone();
            const sphere = this.readSphere(NodesParser.READ_ADDITIONAL_TIGHT_RADIUS);
            const firstPatch = this.getUint32();
            const node = new objects_1.NexusNode(nodeIndex, offset, vertices, faces, error, sphere, firstPatch);
            node.sink = nodeIndex === nodesCount - 1;
            result[nodeIndex] = node;
        }
        return result;
    }
    parseTextures(texturesCount) {
        const texturesData = new Uint32Array(texturesCount);
        for (let textureIndex = 0; textureIndex < texturesCount; textureIndex++) {
            texturesData[textureIndex] = NodesParser.PADDING * this.getUint32();
            this.skipBytes(16 * 4); //skip proj matrix
        }
        // Go through again and set the end to the start of the next texture
        const textures = new Array(texturesCount);
        for (let textureIndex = 0; textureIndex < texturesCount; textureIndex++) {
            const textureStart = texturesData[textureIndex];
            const textureEnd = texturesData[textureIndex + 1];
            textures[textureIndex] = new objects_1.NexusNodeTexture(textureIndex, textureStart, textureEnd);
        }
        return textures;
    }
    parsePatches(header, nodes) {
        const patchesCount = header.patchesCount;
        const patchesData = this.getUint32Array(patchesCount * 3);
        this.skipBytes(patchesCount * 9); // Don't know what we are skipping here
        const result = new Array(patchesCount);
        for (let patchIndex = 0; patchIndex < patchesCount; patchIndex++) {
            const nodeIndex = patchesData[patchIndex * 3];
            const lastTriangle = patchesData[patchIndex * 3 + 1];
            const textureIndex = patchesData[patchIndex * 3 + 2];
            result[patchIndex] = new objects_1.NexusPatch(patchIndex, nodes[nodeIndex], lastTriangle, textureIndex);
        }
        return result;
    }
    static connectPatchesToNodes(nodes, patches) {
        // Assign patches to nodes
        for (let nodeIndex = 0; nodeIndex < nodes.length; nodeIndex++) {
            const node = nodes[nodeIndex];
            const hasNextNode = nodeIndex < nodes.length - 1;
            const lastPatch = hasNextNode ? nodes[nodeIndex + 1].firstPatch : patches.length - 1;
            for (let patchIndex = node.firstPatch; patchIndex < lastPatch; patchIndex++) {
                node.patches.push(patches[patchIndex]);
                patches[patchIndex].node.root = false;
            }
        }
    }
    connectTexturesToPatches(patches, textures) {
        return patches.map(patch => patch.withTexture(textures[patch.texture.id]));
    }
    /**
     * Estimates the size of the nodes by counting vertices, faces and textures.
     * Updates the node size properties in the object.
     */
    static computeAndUpdateNodesSizes(nodes, signature) {
        nodes.forEach(node => node.computeAndUpdateNodeSize(signature.vertex.size, signature.face.size));
    }
    getErrorAndSkipCone() {
        const value = this.getFloat32();
        this.skipBytes(8); //skip cone
        return value;
    }
}
exports.NodesParser = NodesParser;
NodesParser.PADDING = 256;
NodesParser.READ_ADDITIONAL_TIGHT_RADIUS = true;
